/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  FormControl,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import {
  ApiError,
  GarantiasVehicularInmobiliariaResponse,
  PayloadRequestGarantias,
  PayloadRequestGarantiasVehicularInmobiliaria,
} from "@interfaces/index";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { usePatchGuaranteesTypesMutation, usePostGuaranteesTypesMutation } from "@api/garantiasApi";
import { GuaranteeFormHandle } from "./guaranteeFormHandle";

interface AgricolaProps {
  porcentaje: string;
  valor_estimado: string;
  id?: string;
}

interface FormGarantiaAgricolaProps {
  guaranteesData: GarantiasVehicularInmobiliariaResponse[];
  guaranteeTypeId?: string;
  hideSubmitButton?: boolean;
  guaranteeLabel?: string;
  multiFlow?: boolean;
  onStatusChange?: (status: { isValid: boolean; isSubmitting: boolean }) => void;
  loading?: boolean;
  // porcentaje disponible considerando las demás garantías agrícolas (100 si es la única)
  maxPercentage?: number;
  onPercentageChange?: (percentage: number) => void;
}

const FormGarantiaAgricola = forwardRef<GuaranteeFormHandle, FormGarantiaAgricolaProps>(
  (
    {
      guaranteesData,
      guaranteeTypeId,
      hideSubmitButton,
      guaranteeLabel,
      onStatusChange,
      loading = false,
      maxPercentage = 100,
      onPercentageChange,
    }: FormGarantiaAgricolaProps,
    ref,
  ) => {
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useAppDispatch();
    const application = useSelector((state: RootState) => state.register.application);
    const [postGuarantees] = usePostGuaranteesTypesMutation();
    const [patchGuarantees] = usePatchGuaranteesTypesMutation();
    const emptyAgricola: AgricolaProps = {
      porcentaje: "",
      valor_estimado: "",
      id: undefined,
    };

    const [isLoadingState, setIsLoading] = useState(false);
    // se bloquea tanto por su propia carga como cuando el padre está guardando todas las garantías
    const isLoading = isLoadingState || loading;

    const lastSubmitOkRef = useRef(false);

    type FormValues = {
      agricolas: AgricolaProps[];
    };

    const formik = useFormik<FormValues>({
      initialValues: {
        agricolas: [{ ...emptyAgricola }],
      },
      validateOnMount: true,
      validationSchema: yup.object().shape({
        agricolas: yup.array().of(
          yup.object().shape({
            porcentaje: yup
              .string()
              .required("Campo requerido")
              .test("porcentaje-valido", "Ingresa un porcentaje entre 0 y 100", (value) => {
                if (!value) return true;
                const num = Number(value);
                return Number.isFinite(num) && num > 0 && num <= 100;
              })
              .test(
                "porcentaje-disponible",
                `La suma de las garantías no puede superar el 100%. Disponible: ${maxPercentage}%`,
                (value) => {
                  if (!value) return true;
                  return Number(value) <= maxPercentage;
                },
              ),
            valor_estimado: yup.string().required("Campo requerido"),
          }),
        ),
      }),
      onSubmit: async (values, { setSubmitting }) => {
        try {
          lastSubmitOkRef.current = false;
          setIsLoading(true);
          setSubmitting(true);

          const guaranteesToPatch: Array<{ guarantee_id: string; data: PayloadRequestGarantiasVehicularInmobiliaria }> =
            [];
          const guaranteesToPost: PayloadRequestGarantiasVehicularInmobiliaria[] = [];

          values.agricolas.forEach((agricola) => {
            const cleanValue = Number(agricola.valor_estimado.replace(/,/g, "")) || 0;

            const guaranteeItem: PayloadRequestGarantiasVehicularInmobiliaria = {
              loan_application_id: application.id,
              ...(guaranteeTypeId ? { guarantee_type_id: guaranteeTypeId } : {}),
              estimated_value: cleanValue,
              details: {
                percentage: Number(agricola.porcentaje) || 0,
              },
            };

            if (agricola?.id) {
              guaranteesToPatch.push({ guarantee_id: agricola.id, data: guaranteeItem });
            } else {
              guaranteesToPost.push(guaranteeItem);
            }
          });

          // Si existe id => PATCH (uno por garantía). Si no existe => POST (en lista)
          await Promise.all([
            ...guaranteesToPatch.map(({ guarantee_id, data }) =>
              patchGuarantees({ loan_application_id: application.id, guarantee_id, data }).unwrap(),
            ),
            guaranteesToPost.length > 0
              ? postGuarantees({
                  loan_application_id: application.id,
                  data: { guarantees_list: guaranteesToPost } as PayloadRequestGarantias,
                }).unwrap()
              : Promise.resolve(),
          ]);

          lastSubmitOkRef.current = true;
        } catch (error) {
          lastSubmitOkRef.current = false;
          const api_error = error as ApiError;

          Sentry.captureException(error, {
            extra: {
              component: "InfoGarantiaAgricola",
              values: formik.values,
            },
          });

          dispatch(setModalErrorVisible({ open: true, error: api_error }));
        } finally {
          setSubmitting(false);
          setIsLoading(false);
        }
      },
    });

    useEffect(() => {
      onStatusChange?.({ isValid: formik.isValid, isSubmitting: formik.isSubmitting });
    }, [formik.isValid, formik.isSubmitting, onStatusChange]);

    // avisa al padre el porcentaje capturado para que calcule el disponible de las demás garantías
    useEffect(() => {
      const total = formik.values.agricolas.reduce((acc, item) => acc + (Number(item.porcentaje) || 0), 0);
      onPercentageChange?.(total);
    }, [formik.values.agricolas]);

    // si cambia el disponible (otra garantía modificó su porcentaje) se revalida
    useEffect(() => {
      formik.validateForm();
    }, [maxPercentage]);

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          lastSubmitOkRef.current = false;

          const errors = await formik.validateForm();
          if (Object.keys(errors).length > 0) {
            formik.setTouched({
              agricolas: formik.values.agricolas.map(() => ({
                porcentaje: true,
                valor_estimado: true,
              })),
            });
            return false;
          }

          await formik.submitForm();
          return lastSubmitOkRef.current;
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [formik.values.agricolas],
    );

    // valor estimado = monto solicitado × porcentaje / 100 (p.ej. 100 al 10% = 10)
    const calculateEstimatedValue = (percentage: string): string => {
      const percent = Number(percentage);
      const loanAmount = Number(application.loan_amount) || 0;
      if (!percentage || !Number.isFinite(percent) || loanAmount <= 0) return "";

      const estimatedValue = (loanAmount * percent) / 100;
      return estimatedValue.toLocaleString("en-US", { maximumFractionDigits: 2 });
    };

    useEffect(() => {
      const mappedAgricolas = guaranteesData
        .filter((item) => item.guarantee_type_id === guaranteeTypeId && item.active)
        .map((item) => {
          const porcentaje = item.details?.percentage != null ? String(item.details.percentage) : "";

          return {
            porcentaje,
            valor_estimado: calculateEstimatedValue(porcentaje),
            id: item.id,
          };
        });

      if (mappedAgricolas.length > 0) {
        formik.setFieldValue("agricolas", mappedAgricolas);
      }
    }, [guaranteesData]);

    const getFieldError = (fieldName: keyof AgricolaProps, index: number): string | undefined => {
      const agricolaErrors = formik.errors.agricolas as Array<Partial<Record<keyof AgricolaProps, string>>> | undefined;
      if (agricolaErrors && agricolaErrors[index] && agricolaErrors[index][fieldName]) {
        return agricolaErrors[index][fieldName];
      }
      return undefined;
    };

    const handleChangePercentage = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      // solo números con hasta 2 decimales y sin pasar del 100%
      const rawValue = e.target.value.replace(/[^0-9.]/g, "");
      if (!/^\d{0,3}(\.\d{0,2})?$/.test(rawValue)) return;
      if (Number(rawValue) > Math.min(100, maxPercentage)) return;
      // se actualizan ambos campos en una sola llamada para que la validación corra con los dos valores
      formik.setFieldValue(`agricolas[${index}]`, {
        ...formik.values.agricolas[index],
        porcentaje: rawValue,
        valor_estimado: calculateEstimatedValue(rawValue),
      });
    };

    return (
      <Box>
        <form onSubmit={formik.handleSubmit} noValidate>
          <Typography
            mt={isMobileOrTablet ? 2 : 3}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color="primary.dark"
            textAlign={"center"}
          >
            Detalle de tu garantía {guaranteeLabel}
          </Typography>

          {formik.values.agricolas.map((agricola: AgricolaProps, index: number) => (
            <Box key={index} mt={index > 0 ? 4 : 0}>
              <FormControl fullWidth>
                <TextField
                  id={`porcentaje-${index}`}
                  name={`agricolas[${index}].porcentaje`}
                  label="Porcentaje"
                  margin="normal"
                  fullWidth
                  required
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangePercentage(e, index)}
                  inputProps={{ maxLength: 6, inputMode: "decimal" }}
                  value={agricola.porcentaje}
                  disabled={formik.isSubmitting || isLoading}
                  error={Boolean(formik.touched.agricolas?.[index]?.porcentaje && getFieldError("porcentaje", index))}
                  helperText={formik.touched.agricolas?.[index]?.porcentaje ? getFieldError("porcentaje", index) : ""}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  id={`valor_estimado-${index}`}
                  name={`agricolas[${index}].valor_estimado`}
                  label="Valor Estimado"
                  margin="normal"
                  fullWidth
                  required
                  autoComplete="off"
                  value={agricola.valor_estimado}
                  disabled={formik.isSubmitting || isLoading}
                  error={Boolean(
                    formik.touched.agricolas?.[index]?.valor_estimado && getFieldError("valor_estimado", index),
                  )}
                  helperText={
                    formik.touched.agricolas?.[index]?.valor_estimado ? getFieldError("valor_estimado", index) : ""
                  }
                  InputProps={{
                    // se calcula a partir del porcentaje y el monto solicitado
                    readOnly: true,
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </FormControl>
            </Box>
          ))}

          {!hideSubmitButton && (
            <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
              <Button
                variant="default"
                type="submit"
                color="primary"
                disabled={formik.isSubmitting || isLoading || !formik.isValid}
              >
                Continuar
              </Button>
            </Box>
          )}
        </form>
      </Box>
    );
  },
);

FormGarantiaAgricola.displayName = "FormGarantiaAgricola";

export default FormGarantiaAgricola;
